import React, { useEffect, useMemo, useState } from 'react';
import type { AgentConfiguratorState } from '../types/agentConfigurator';
import { EVALUATION_MODE_LABELS, FRONTEND_EVALUATION_MODES } from '../types/optimizationJob';
import type { EvaluationMode, SelectionScenario } from '../types/optimizationJob';
import { evaluationModeHasEvaluatorParams } from '../types/evaluation';
import type {
  AgentOptimizationMode,
  OptimizationPolicyUiState,
} from '../utils/optimizationPolicy';
import {
  applyBulkFrozen,
  applyBulkModifiableScenario,
  listOptimizationAgentTargets,
  listOptimizationMetaPromptTargets,
} from '../utils/optimizationPolicy';
import OptimizationDatasetPicker from './OptimizationDatasetPicker';
import EvaluatorParamsFields from './EvaluatorParamsFields';
import {
  CONFIG_TUNING_APPROACH_OPTIONS,
  OPTIMIZATION_APPROACH_LABELS,
  isMutationsApproach,
} from '../types/optimizationApproach';
import './ConnectorConfigTuningFields.css';
import type { EvaluatorParamsFormState } from '../types/evaluation';

export interface ConnectorConfigTuningFieldsProps {
  currentConfig: AgentConfiguratorState;
  onConfigChange: (next: AgentConfiguratorState) => void;
  disabled?: boolean;
  datasets: string[];
  selectedDatasets: string[];
  onDatasetsChange: (names: string[]) => void;
  datasetsLoading?: boolean;
  iterations: number;
  onIterationsChange: (value: number) => void;
  optimizationPolicyUi: OptimizationPolicyUiState;
  onOptimizationPolicyUiChange: (next: OptimizationPolicyUiState) => void;
  selectionScenario: SelectionScenario;
  onSelectionScenarioChange: (value: SelectionScenario) => void;
  evaluationMode: EvaluationMode;
  onEvaluationModeChange: (value: EvaluationMode) => void;
  evaluatorParams: EvaluatorParamsFormState;
  onEvaluatorParamsChange: (value: EvaluatorParamsFormState) => void;
  /** Models for LLM judge picker (Profile → Models). */
  availableModels?: string[];
  /** Pipeline mode: datasets come from select_training_datasets step. */
  hideDatasetPicker?: boolean;
}

function SegmentGroup({
  label,
  labelId,
  disabled,
  children,
}: {
  label: string;
  labelId: string;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="cct-control">
      <span className="cct-label" id={labelId}>
        {label}
      </span>
      <div className="cct-segment" role="group" aria-labelledby={labelId}>
        {children}
      </div>
    </div>
  );
}

function SegmentButton({
  active,
  disabled,
  onClick,
  children,
  title,
}: {
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
  title?: string;
}) {
  return (
    <button
      type="button"
      className={`cct-segment__btn${active ? ' cct-segment__btn--active' : ''}`}
      disabled={disabled}
      onClick={onClick}
      title={title}
    >
      {children}
    </button>
  );
}

function OptimizationModeControl({
  label,
  labelId,
  mode,
  disabled,
  onChange,
}: {
  label: string;
  labelId: string;
  mode: AgentOptimizationMode;
  disabled?: boolean;
  onChange: (mode: AgentOptimizationMode) => void;
}) {
  return (
    <div className="cct-policy-row">
      <span className="cct-policy-row__label" id={labelId}>
        {label}
      </span>
      <div className="cct-segment cct-segment--3" role="group" aria-labelledby={labelId}>
        <SegmentButton
          active={mode === 'constant'}
          disabled={disabled}
          title="Frozen — agent stays in runtime but will not be tuned"
          onClick={() => onChange('constant')}
        >
          Frozen
        </SegmentButton>
        <SegmentButton
          active={mode === 'base'}
          disabled={disabled}
          title="Base optimization — quality-focused tuning"
          onClick={() => onChange('base')}
        >
          Base
        </SegmentButton>
        <SegmentButton
          active={mode === 'optimize_by_token'}
          disabled={disabled}
          title="Optimize by token — token-aware tuning"
          onClick={() => onChange('optimize_by_token')}
        >
          Token
        </SegmentButton>
      </div>
    </div>
  );
}

function agentRoleLabel(role: 'coordinator' | 'worker' | 'async_orchestrator'): string {
  if (role === 'coordinator') return 'Coordinator';
  if (role === 'async_orchestrator') return 'Async orchestrator';
  return 'Worker';
}

const ITERATIONS_MIN = 1;
const ITERATIONS_MAX = 10;

function clampIterations(value: number): number {
  return Math.min(ITERATIONS_MAX, Math.max(ITERATIONS_MIN, value));
}

const ConnectorConfigTuningFields: React.FC<ConnectorConfigTuningFieldsProps> = ({
  currentConfig,
  onConfigChange,
  disabled = false,
  datasets,
  selectedDatasets,
  onDatasetsChange,
  datasetsLoading = false,
  iterations,
  onIterationsChange,
  optimizationPolicyUi,
  onOptimizationPolicyUiChange,
  selectionScenario,
  onSelectionScenarioChange,
  evaluationMode,
  onEvaluationModeChange,
  evaluatorParams,
  onEvaluatorParamsChange,
  availableModels,
  hideDatasetPicker = false,
}) => {
  const approach = currentConfig.approach || 'BASELINE';
  const mutationsApproach = isMutationsApproach(approach);
  const effectiveEvaluationMode: EvaluationMode = mutationsApproach ? 'config-scorer' : evaluationMode;

  useEffect(() => {
    if (mutationsApproach && evaluationMode !== 'config-scorer') {
      onEvaluationModeChange('config-scorer');
    }
  }, [mutationsApproach, evaluationMode, onEvaluationModeChange]);
  const agentTargets = useMemo(
    () => listOptimizationAgentTargets(currentConfig),
    [currentConfig]
  );
  const metaPromptTargets = useMemo(
    () => listOptimizationMetaPromptTargets(currentConfig),
    [currentConfig]
  );

  const [iterationsInput, setIterationsInput] = useState(String(iterations));

  useEffect(() => {
    setIterationsInput(String(iterations));
  }, [iterations]);

  const setAgentMode = (key: string, mode: AgentOptimizationMode) => {
    onOptimizationPolicyUiChange({
      ...optimizationPolicyUi,
      agents: { ...optimizationPolicyUi.agents, [key]: mode },
    });
  };

  const setMetaPromptMode = (key: string, mode: AgentOptimizationMode) => {
    onOptimizationPolicyUiChange({
      ...optimizationPolicyUi,
      metaPrompts: { ...optimizationPolicyUi.metaPrompts, [key]: mode },
    });
  };

  return (
    <div className="cct-form">
      <section className="cct-card">
        <h4 className="cct-card__title cct-card__title--with-icon">
          <span className="cct-card__title-icon" aria-hidden>
            <i className="fas fa-sliders" />
          </span>
          Optimization
        </h4>
        <SegmentGroup label="Approach" labelId="config-tuning-approach-label" disabled={disabled}>
          {CONFIG_TUNING_APPROACH_OPTIONS.map((option) => (
            <SegmentButton
              key={option}
              active={approach === option}
              disabled={disabled}
              title={option}
              onClick={() => {
                onConfigChange({ ...currentConfig, approach: option });
              }}
            >
              {OPTIMIZATION_APPROACH_LABELS[option]}
            </SegmentButton>
          ))}
        </SegmentGroup>

        <div className="cct-card__grid cct-card__grid--2 cct-card__grid--stack-sm">
          <div className="cct-control">
            <span className="cct-label" id="config-tuning-selection-label">
              Candidate selection
            </span>
            <div
              className="cct-segment cct-segment--3"
              role="group"
              aria-labelledby="config-tuning-selection-label"
            >
              <SegmentButton
                active={selectionScenario === 'quality'}
                disabled={disabled}
                title="Select the best candidate by quality score"
                onClick={() => onSelectionScenarioChange('quality')}
              >
                Quality
              </SegmentButton>
              <SegmentButton
                active={selectionScenario === 'tokens'}
                disabled={disabled}
                title="Select the best candidate by actor token usage"
                onClick={() => onSelectionScenarioChange('tokens')}
              >
                Tokens
              </SegmentButton>
              <SegmentButton
                active={selectionScenario === 'cost'}
                disabled={disabled}
                title="Select the best candidate by cost"
                onClick={() => onSelectionScenarioChange('cost')}
              >
                Cost
              </SegmentButton>
            </div>
          </div>

          <div className="cct-control">
            <span className="cct-label" id="config-tuning-evaluation-mode-label">
              Evaluation mode
            </span>
            {mutationsApproach ? (
              <p className="cct-policy-hint" style={{ margin: 0 }}>
                {EVALUATION_MODE_LABELS['config-scorer']} (required for Mutations approach)
              </p>
            ) : (
              <select
                id="config-tuning-evaluation-mode"
                className="cct-input cct-select"
                value={evaluationMode}
                disabled={disabled}
                aria-labelledby="config-tuning-evaluation-mode-label"
                onChange={(e) => onEvaluationModeChange(e.target.value as EvaluationMode)}
              >
                {FRONTEND_EVALUATION_MODES.map((mode) => (
                  <option key={mode} value={mode}>
                    {EVALUATION_MODE_LABELS[mode]}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>
      </section>

      {evaluationModeHasEvaluatorParams(effectiveEvaluationMode) ? (
        <section className="cct-card cct-card--compact">
          <h4 className="cct-card__title cct-card__title--with-icon">
            <span className="cct-card__title-icon" aria-hidden>
              <i className="fas fa-gauge-high" />
            </span>
            Evaluator parameters
          </h4>
          <p className="cct-policy-hint">
            Parameters depend on the evaluation mode. Direction applies to candidate selection during
            tuning (llm-judge and algorithmic).
          </p>
          <EvaluatorParamsFields
            idPrefix="config-tuning-evaluator"
            mode={effectiveEvaluationMode}
            value={evaluatorParams}
            onChange={onEvaluatorParamsChange}
            disabled={disabled}
            includeDirection
            availableModels={availableModels}
          />
        </section>
      ) : null}

      {!hideDatasetPicker ? (
        <section className="cct-card cct-card--datasets">
          <div className="cct-card__head">
            <h4 className="cct-card__title cct-card__title--with-icon">
              <span className="cct-card__title-icon" aria-hidden>
                <i className="fas fa-database" />
              </span>
              Datasets
            </h4>
            {datasets.length > 0 ? (
              <span className="cct-badge">
                {selectedDatasets.length} / {datasets.length}
              </span>
            ) : null}
          </div>
          <OptimizationDatasetPicker
            className="opt-dataset-picker--cct"
            datasets={datasets}
            selected={selectedDatasets}
            onChange={onDatasetsChange}
            disabled={disabled}
            loading={datasetsLoading}
            emptyMessage="No datasets — import or wait for sync"
          />
        </section>
      ) : null}

      <section className="cct-card cct-card--compact">
        <h4 className="cct-card__title cct-card__title--with-icon">
          <span className="cct-card__title-icon" aria-hidden>
            <i className="fas fa-rotate" />
          </span>
          Iterations
        </h4>
        <div className="cct-control">
          <span className="cct-label" id="config-tuning-iterations-label">
            Number of optimization steps
          </span>
          <input
            type="number"
            className="cct-input"
            min={ITERATIONS_MIN}
            max={ITERATIONS_MAX}
            value={iterationsInput}
            aria-labelledby="config-tuning-iterations-label"
            onChange={(e) => {
              if (disabled) return;
              const digitsOnly = e.target.value.replace(/\D/g, '');
              if (digitsOnly === '') {
                setIterationsInput('');
                return;
              }
              if (digitsOnly.length > 1 && digitsOnly.startsWith('0')) return;

              const numValue = Number(digitsOnly);
              if (numValue <= ITERATIONS_MAX) {
                setIterationsInput(digitsOnly);
                if (numValue >= ITERATIONS_MIN) {
                  onIterationsChange(numValue);
                }
              } else {
                setIterationsInput(String(ITERATIONS_MAX));
                onIterationsChange(ITERATIONS_MAX);
              }
            }}
            onBlur={() => {
              if (disabled) return;
              const trimmed = iterationsInput.trim();
              if (!trimmed) {
                setIterationsInput(String(ITERATIONS_MIN));
                onIterationsChange(ITERATIONS_MIN);
                return;
              }
              const numValue = clampIterations(Number(trimmed));
              setIterationsInput(String(numValue));
              onIterationsChange(numValue);
            }}
            disabled={disabled}
          />
        </div>
      </section>

      <section className="cct-card">
        <div className="cct-card__head">
          <h4 className="cct-card__title cct-card__title--with-icon">
            <span className="cct-card__title-icon" aria-hidden>
              <i className="fas fa-layer-group" />
            </span>
            Optimization policy
          </h4>
          <div className="cct-policy-bulk">
            <button
              type="button"
              className="cct-policy-bulk__btn"
              disabled={disabled}
              onClick={() => onOptimizationPolicyUiChange(applyBulkFrozen(optimizationPolicyUi, currentConfig))}
            >
              All → Frozen
            </button>
            <button
              type="button"
              className="cct-policy-bulk__btn"
              disabled={disabled}
              onClick={() =>
                onOptimizationPolicyUiChange(
                  applyBulkModifiableScenario(optimizationPolicyUi, 'base', currentConfig)
                )
              }
            >
              All → Base
            </button>
            <button
              type="button"
              className="cct-policy-bulk__btn"
              disabled={disabled}
              onClick={() =>
                onOptimizationPolicyUiChange(
                  applyBulkModifiableScenario(optimizationPolicyUi, 'optimize_by_token', currentConfig)
                )
              }
            >
              All → Token
            </button>
          </div>
        </div>
        <p className="cct-policy-hint">
          Choose what can be tuned and which optimization scenario applies. Frozen targets stay in
          runtime but are not modified.
        </p>
        {agentTargets.length > 0 ? (
          <div className="cct-policy-agents">
            <h5 className="cct-policy-agents__title">
              <span className="cct-policy-agents__title-icon" aria-hidden>
                <i className="fas fa-robot" />
              </span>
              Agents
            </h5>
            <div className="cct-policy-list cct-policy-list--agents">
              {agentTargets.map((target) => (
                <OptimizationModeControl
                  key={target.key}
                  label={`${target.name} (${agentRoleLabel(target.role)})`}
                  labelId={`config-tuning-agent-${target.key}`}
                  mode={optimizationPolicyUi.agents[target.key] ?? 'base'}
                  disabled={disabled}
                  onChange={(mode) => setAgentMode(target.key, mode)}
                />
              ))}
            </div>
          </div>
        ) : null}
        {metaPromptTargets.length > 0 ? (
          <>
            <h5 className="cct-policy-subtitle">Meta-prompts</h5>
            <div className="cct-policy-list cct-policy-list--meta">
              {metaPromptTargets.map((target) => (
                <OptimizationModeControl
                  key={target.key}
                  label={target.label}
                  labelId={`config-tuning-meta-${target.key}`}
                  mode={optimizationPolicyUi.metaPrompts[target.key] ?? 'base'}
                  disabled={disabled}
                  onChange={(mode) => setMetaPromptMode(target.key, mode)}
                />
              ))}
            </div>
          </>
        ) : null}
      </section>
    </div>
  );
};

export default ConnectorConfigTuningFields;
